import { lazy } from "react";

const CashMgmts = lazy(() =>
  import("../Accounts/CashManagement/CashMgmts")
);
const ExpHome = lazy(() => import("../Accounts/Expenses/ExpHome"));
const JournalVouchers = lazy(() =>
  import("../Accounts/JournalVoucher/JournalVouchers")
);
const AddEditVoucher = lazy(() =>
  import("../Accounts/JournalVoucher/AddEditForm/AddEditVoucher")
);
const JournalEntries = lazy(() =>
  import("../Accounts/JournalEntries/JournalEntries")
);
const FiscalYears = lazy(() =>
  import("../Accounts/FiscalYear/FiscalYears")
);
const Snapshots = lazy(() =>
  import("../Accounts/FiscalYear/Snapshots/Snapshots")
);
const TakeSnapshot = lazy(() =>
  import("../Accounts/FiscalYear/Snapshots/TakeSnapshot/TakeSnapshot")
);
const BalanceSheet = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.BalanceSheet }))
);
const TrialBalance = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.TrialBalance }))
);
const SupplierAging = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.SupplierAging }))
);
const CustomerAging = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.CustomerAging }))
);
const GeneralLedger = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.GeneralLedger }))
);
const ProfitAndLoss = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.ProfitAndLoss }))
);
const CoaManagement = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.CoaManagement }))
);
const AccountMapping = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.AccountMappingPage }))
);
const CashBankDashboard = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.CashBankDashboard }))
);
const AccountTransfers = lazy(() =>
  import("../Accounts/AccountTransfer/AccountTransfers")
);
const BankAccounts = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.BankAccounts }))
);
const CashSafes = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.CashSafes }))
);
const CashSafeLedger = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.CashSafeLedger }))
);
// The branch custodian's landing screen — their own outlet's safe, no list.
const MyCashSafe = lazy(() =>
  import("../Accounts").then((m) => ({ default: m.MyCashSafe }))
);
const BankReconciliations = lazy(() =>
  import("../Accounts/BankReconciliation/BankReconciliations")
);
const ReconciliationSession = lazy(() =>
  import("../Accounts/BankReconciliation/Session/ReconciliationSession")
);

export const accountsRoutes = (access, settings) => {
  const routes = [];

  if (access?.fyAccess) {
    routes.push({ path: "/fiscal_year", element: FiscalYears, title: "fiscalYear" });
    if (access?.fySnapshotAccess) {
      routes.push(
        { path: "/fiscal_year/fy_snapshots/:id", element: Snapshots, title: "fiscalYearSnapshots" },
        { path: "/fiscal_year/take_snapshot/:id", element: TakeSnapshot, title: "takeSnapshot" },
      );
    }
  }

  if (!settings?.fiscalYearId) return routes;

  if (access.cashMgmtAccess) {
    routes.push({ path: "/cash_management", element: CashMgmts, title: "cashManagement" });
  }

  // The treasury dashboard discloses the org's whole cash + bank position, so it
  // is its own grant rather than a side-effect of till-level cash management.
  if (access?.cashBankDashboardAccess) {
    routes.push({ path: "/cash_bank_dashboard", element: CashBankDashboard, title: "cashBankDashboard" });
  }

  if (access.expenseAccess) {
    routes.push({ path: "/expenses", element: ExpHome, title: "expenses" });
  }

  if (access?.journalVoucherAccess) {
    routes.push(
      { path: "/journal_voucher", element: JournalVouchers, title: "journalVouchers" },
      { path: "/journal_voucher_form", element: AddEditVoucher, title: "addJournalVoucher" },
      { path: "/journal_voucher_form/:id", element: AddEditVoucher, title: "editJournalVoucher" },
    );
  }

  if (access?.reportAccess) {
    routes.push(
      { path: "/balance_sheet", element: BalanceSheet, title: "balanceSheet", enabled: access.report?.balanceSheet },
      { path: "/trial_balance", element: TrialBalance, title: "trialBalance", enabled: access.report?.trialBalance },
      { path: "/supplier_aging", element: SupplierAging, title: "supplierAging", enabled: access.report?.supplierAging },
      { path: "/customer_aging", element: CustomerAging, title: "customerAging", enabled: access.report?.customerAging },
      // General Ledger and Journal Entries are the RAW ledger. They used to ride on
      // profitAndLoss, so granting a manager the P&L silently handed them every
      // posting in the business. Own flag since 2026-08-13, backfilled from
      // profitAndLoss so nobody's access changed on deploy.
      { path: "/general_ledger", element: GeneralLedger, title: "generalLedger", enabled: access.report?.generalLedger },
      { path: "/journal_entries", element: JournalEntries, title: "journalEntries", enabled: access.report?.generalLedger },
      { path: "/profit_and_loss", element: ProfitAndLoss, title: "profitAndLoss", enabled: access.report?.profitAndLoss },
    );
  }

  if (access?.coaAccess) {
    routes.push({ path: "/coa_management", element: CoaManagement, title: "chartOfAccounts" });
  }

  if (access?.accountMappingAccess) {
    routes.push({ path: "/account_mapping", element: AccountMapping, title: "accountMapping" });
  }

  // Each on its own flag. These were previously granted to anyone with COA OR
  // Cash Management access, which leaked treasury screens to cashiers.
  if (access?.accountTransferAccess) {
    routes.push({ path: "/account_transfers", element: AccountTransfers, title: "accountTransfers" });
  }

  if (access?.bankAccountAccess) {
    routes.push({ path: "/bank_accounts", element: BankAccounts, title: "bankAccounts" });
  }

  // The physical cash locker that funds supplier payments. Its own grant — holding
  // the shop's cash is a different responsibility from moving money between GL
  // accounts or maintaining bank records.
  if (access?.cashSafeAccess) {
    routes.push(
      { path: "/cash_safes", element: CashSafes, title: "cashSafes" },
      { path: "/cash_safe_ledger/:id", element: CashSafeLedger, title: "cashSafeLedger" },
    );
  }

  // Separate grant, separate screen: the person responsible for ONE branch's safe
  // never sees the org-wide management list.
  if (access?.branchCashSafeAccess) {
    routes.push({ path: "/my_cash_safe", element: MyCashSafe, title: "myCashSafe" });
  }

  if (access?.bankReconciliationAccess) {
    routes.push(
      { path: "/bank_reconciliation", element: BankReconciliations, title: "bankReconciliation" },
      { path: "/bank_reconciliation/session/:id", element: ReconciliationSession, title: "bankReconciliationSession" },
    );
  }

  return routes;
};
